/**
 * Shared password policy for the admin portal.
 *
 * One source of truth for both the client (login, profile, club-admin dialogs)
 * and the server (create-club-admin Edge Function mirrors this rule). Keep the
 * rule here so the two never drift apart.
 */

export const PASSWORD_MIN = 10;

export interface PasswordCheck {
  ok: boolean;
  message?: string;
}

/**
 * Minimum-strength check: at least PASSWORD_MIN characters, and at least one
 * letter and one digit. Intentionally lenient beyond that (symbols optional)
 * so it never blocks a strong passphrase.
 */
export function validatePassword(pw: string): PasswordCheck {
  if (typeof pw !== 'string' || pw.length < PASSWORD_MIN) {
    return { ok: false, message: `Password must be at least ${PASSWORD_MIN} characters` };
  }
  if (!/[a-zA-Z]/.test(pw)) {
    return { ok: false, message: 'Password must contain at least one letter' };
  }
  if (!/[0-9]/.test(pw)) {
    return { ok: false, message: 'Password must contain at least one number' };
  }
  return { ok: true };
}

const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // no I/O — avoid look-alikes
const LOWER = 'abcdefghijkmnpqrstuvwxyz'; // no l/o
const DIGITS = '23456789'; // no 0/1
const SYMBOLS = '!@#$%^&*-_=+?';
const ALL = UPPER + LOWER + DIGITS + SYMBOLS;

/** Pick one character from `set` using a uniform, crypto-strong draw. */
function pick(set: string): string {
  const arr = new Uint32Array(1);
  // Rejection-sample to avoid modulo bias.
  const limit = Math.floor(0xffffffff / set.length) * set.length;
  let n: number;
  do {
    crypto.getRandomValues(arr);
    n = arr[0];
  } while (n >= limit);
  return set[n % set.length];
}

/** Fisher–Yates shuffle using crypto-strong draws. */
function shuffle(chars: string[]): string[] {
  for (let i = chars.length - 1; i > 0; i--) {
    const arr = new Uint32Array(1);
    const limit = Math.floor(0xffffffff / (i + 1)) * (i + 1);
    let n: number;
    do {
      crypto.getRandomValues(arr);
      n = arr[0];
    } while (n >= limit);
    const j = n % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars;
}

/**
 * Generate a 16-character password from crypto.getRandomValues with a
 * guaranteed one of each: uppercase, lowercase, digit, symbol. Always passes
 * validatePassword().
 */
export function generateStrongPassword(): string {
  const length = 16;
  const required = [pick(UPPER), pick(LOWER), pick(DIGITS), pick(SYMBOLS)];
  const rest: string[] = [];
  for (let i = required.length; i < length; i++) {
    rest.push(pick(ALL));
  }
  return shuffle([...required, ...rest]).join('');
}
